import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiService } from '../services/apiService';

export type UserRole = 'company' | 'cxo' | 'member' | 'admin';
type AuthProvider = 'email' | 'linkedin';

interface LinkedInData {
  id: string;
  verified: boolean;
  profileUrl?: string;
  headline?: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  roles: UserRole[];
  avatar?: string;
  avatarUrl?: string;
  authProvider: AuthProvider;
  linkedin?: LinkedInData;
  linkedinId?: string;
  linkedinVerified?: boolean;
  linkedinProfileUrl?: string;
  linkedinHeadline?: string;
  cxoProfile?: {
    headline?: string;
    bio?: string;
    city?: string;
    country?: string;
    yearsOfExperience?: number;
    executiveFunctions?: string[];
    industries?: string[];
    skills?: string[];
    specializations?: string[];
    languages?: string[];
  };
  companyProfile?: {
    id: string;
    companyName?: string;
    registrationNumber?: string;
    industry?: string;
    companySize?: string;
    website?: string;
    address?: string;
    description?: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, role: UserRole) => Promise<{ data?: { user: User; token: string } }>;
  linkedinLogin: (role: UserRole) => Promise<void>;
  linkedinConnect: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  linkedinRegister: (role: UserRole) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  error: string | null;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  companyName?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    const token = apiService.getToken();
    if (token) {
      apiService.getCurrentUser()
        .then((response) => {
          if (response.success && response.data?.user) {
            setUser(response.data.user as User);
            // Store user in localStorage for ProtectedRoute access
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
        })
        .catch((err) => {
          console.error('[AuthContext] getCurrentUser error:', err);
          apiService.removeToken();
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string, role: UserRole): Promise<{data?: {user: User; token: string}}> => {
    setError(null);
    try {
      const response = await apiService.login({ email, password, role });
      
      if (response.success && response.data) {
        apiService.setToken(response.data.token);
        const userData = {
          ...response.data.user,
          authProvider: 'email',
        } as User;
        setUser(userData);
        return { data: { user: userData, token: response.data.token } };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    }
  }, []);

  const linkedinLogin = useCallback(async (role: UserRole) => {
    setError(null);
    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
        `response_type=code` +
        `&client_id=${import.meta.env.VITE_LINKEDIN_CLIENT_ID || ''}` +
        `&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/linkedin/callback')}` +
        `&scope=openid%20profile%20email` +
        `&state=${encodeURIComponent(JSON.stringify({ role, action: 'login' }))}`;

      const popup = window.open(
        linkedinAuthUrl,
        'LinkedIn Auth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for LinkedIn authentication.');
      }

      return new Promise<void>((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            apiService.setToken(event.data.token);
            setUser({
              ...event.data.user,
              authProvider: 'linkedin',
            } as User);
            resolve();
          } else if (event.data.type === 'LINKEDIN_AUTH_ERROR') {
            window.removeEventListener('message', handleMessage);
            reject(new Error(event.data.error || 'LinkedIn authentication failed'));
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          popup.close();
          reject(new Error('LinkedIn authentication timed out'));
        }, 300000);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LinkedIn login failed';
      setError(message);
      throw err;
    }
  }, []);

  const linkedinConnect = useCallback(async () => {
    if (!user) throw new Error('Must be logged in to connect LinkedIn');
    
    setError(null);
    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
        `response_type=code` +
        `&client_id=${import.meta.env.VITE_LINKEDIN_CLIENT_ID || ''}` +
        `&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/linkedin/callback')}` +
        `&scope=openid%20profile%20email` +
        `&state=${encodeURIComponent(JSON.stringify({ userId: user.id, action: 'connect' }))}`;

      const popup = window.open(
        linkedinAuthUrl,
        'LinkedIn Auth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for LinkedIn authentication.');
      }

      return new Promise<void>((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'LINKEDIN_CONNECT_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            setUser({
              ...user,
              linkedin: event.data.linkedin,
            });
            resolve();
          } else if (event.data.type === 'LINKEDIN_AUTH_ERROR') {
            window.removeEventListener('message', handleMessage);
            reject(new Error(event.data.error || 'LinkedIn connection failed'));
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          popup.close();
          reject(new Error('LinkedIn connection timed out'));
        }, 300000);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LinkedIn connection failed';
      setError(message);
      throw err;
    }
  }, [user]);

  const register = useCallback(async (data: RegisterData) => {
    setError(null);
    try {
      const response = await apiService.register(data);
      
      if (response.success && response.data) {
        apiService.setToken(response.data.token);
        setUser({
          ...response.data.user,
          authProvider: 'email',
        } as User);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
      throw err;
    }
  }, []);

  const linkedinRegister = useCallback(async (role: UserRole) => {
    setError(null);
    try {
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
        `response_type=code` +
        `&client_id=${import.meta.env.VITE_LINKEDIN_CLIENT_ID || ''}` +
        `&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/linkedin/callback')}` +
        `&scope=openid%20profile%20email` +
        `&state=${encodeURIComponent(JSON.stringify({ role, action: 'register' }))}`;

      const popup = window.open(
        linkedinAuthUrl,
        'LinkedIn Auth',
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for LinkedIn authentication.');
      }

      return new Promise<void>((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
            window.removeEventListener('message', handleMessage);
            apiService.setToken(event.data.token);
            setUser({
              ...event.data.user,
              authProvider: 'linkedin',
            } as User);
            resolve();
          } else if (event.data.type === 'LINKEDIN_AUTH_ERROR') {
            window.removeEventListener('message', handleMessage);
            reject(new Error(event.data.error || 'LinkedIn authentication failed'));
          }
        };

        window.addEventListener('message', handleMessage);

        setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          popup.close();
          reject(new Error('LinkedIn authentication timed out'));
        }, 300000);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'LinkedIn registration failed';
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    apiService.removeToken();
    setUser(null);
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiService.getCurrentUser();
      if (response.success && response.data?.user) {
        const userData = response.data.user;
        setUser((prev) => ({
          ...userData,
          authProvider: prev?.authProvider || 'email',
          linkedin: userData.linkedinVerified ? {
            id: userData.linkedinId || prev?.linkedin?.id || '',
            verified: userData.linkedinVerified,
            profileUrl: userData.linkedinProfileUrl || prev?.linkedin?.profileUrl,
            headline: userData.linkedinHeadline || prev?.linkedin?.headline,
          } : prev?.linkedin,
        } as User));
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        linkedinLogin,
        linkedinConnect,
        register,
        linkedinRegister,
        logout,
        refreshUser,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
